import React, { useEffect, useState } from "react";
import "./createprofile.scss";
import { logodarkimg, profileimg } from "../../../assets/images";
import {
  callicon,
  editicon,
  emailicon,
  messageicon,
  profileicon,
} from "../../../assets/icons";
import Select from "react-select";
import { FaArrowRightLong, FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { Modal } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { RxCross2 } from "react-icons/rx";
import OTPInput from "react-otp-input";
import { set, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  createProfileAdditionalDetailValidation,
  createProfileValidation,
} from "../../../validations";
import PhoneInput from "react-phone-input-2";
import { useMutation, useQuery } from "@tanstack/react-query";
import useSnackbar from "../../../hooks/useSnackbar";
import { AuthService, PublicService } from "../../../services";
import { decryptData, generateFormDataPayload } from "../../../utils/helper";
import useUser from "../../../hooks/useUser";
import { getValue } from "@testing-library/user-event/dist/utils";
import ButtonLoader from "../../../component/buttonLoader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { Helmet } from "react-helmet";

const bloodGroups = [
  {
    label: "A+",
    value: "A+",
  },
  {
    label: "B+",
    value: "B+",
  },
  {
    label: "A-",
    value: "A-",
  },
  {
    label: "B-",
    value: "B-",
  },
  {
    label: "AB+",
    value: "AB+",
  },
  {
    label: "AB-",
    value: "AB-",
  },
  {
    label: "O+",
    value: "O+",
  },
  {
    label: "O-",
    value: "O-",
  },
];

const CreateProfile = () => {
  const location = useLocation();
  const { setUser } = useUser();
  const [otp, setOtp] = useState("");
  const [seconds, setSeconds] = useState(60);
  const navigate = useNavigate();
  const { snackbar } = useSnackbar();
  const { addUser, sendOtp, verifyOtp } = AuthService();
  const { getCountriesApi } = PublicService();
  const [modalShow, setModalShow] = useState(false);
  const [step, setStep] = useState(1);
  const [changePassword, setChangePassword] = useState(true);
  const changeIcon = !changePassword;

  const countriesData =
    useQuery({
      queryKey: ["countries"],
      queryFn: () => getCountriesApi(),
    }).data?.data?.countries || [];

  const countries = countriesData?.map((country) => ({
    label: country.countryName,
    value: country.countryName,
  }));

  const {
    register,
    getValues,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(createProfileValidation),
    defaultValues: {
      email: "",
      name: "",
      mobile: "",
      countryCode: "91",
      password: "",
      country: "",
      isEmailVerified: false,
      isMobileVerified: false,
    },
  });

  const {
    watch: additionalWatch,
    setValue: additionalSetValue,
    register: additionalRegister,
    handleSubmit: additionalHandleSubmit,
    formState: { errors: additionalErrors },
  } = useForm({
    resolver: joiResolver(createProfileAdditionalDetailValidation),
    defaultValues: {
      dob: moment().subtract(13, "years").toISOString(),
      bloodGroup: "",
      height: "",
      weight: "",
      emergencyContact: "",
      address: "",
    },
  });

  const dob = additionalWatch("dob");
  const emergencyContact = additionalWatch("emergencyContact");
  const countryCode = watch("countryCode");
  const mobile = watch("mobile");
  const profileImage = watch("profile");
  const isMobileVerified = watch("isMobileVerified");
  const profileImageUrl = profileImage?.name
    ? URL.createObjectURL(profileImage)
    : profileimg;

  useEffect(() => {
    if (location.state?.isEmailVerified) {
      setValue("isEmailVerified", true);
      setValue("email", location.state?.email);
    }

    if (location.state?.isMobileVerified) {
      setValue("isMobileVerified", true);
      setValue("mobile", location.state?.mobile);
      setValue("countryCode", location.state?.countryCode);
    }
  }, [location]);

  useEffect(() => {
    if (seconds < 60) {
      return;
    }

    const intervalId = setInterval(() => {
      setSeconds((prevTime) => {
        if (prevTime === 0) {
          clearInterval(intervalId);
          return 0;
        }

        return prevTime - 1;
      });
    }, 1000);
  }, [seconds]);

  const sendOtpMutation = useMutation({
    mutationFn: (data) => {
      return sendOtp({ ...data, type: "otp" });
    },
    onSuccess: (data) => {
      setSeconds(60);
      snackbar(data?.message || "", "info");
    },
    onError: (err) => {
      snackbar(err.data.message || "");
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: (data) => {
      if (otp.length !== 6) {
        snackbar("Please enter valid otp code" || "");
        return null;
      }

      return verifyOtp(data);
    },
    onSuccess: (data) => {
      if (data) {
        setValue("isMobileVerified", true);
        setModalShow(false);
        snackbar(data?.message || "", "info");
      }
    },
    onError: (err) => {
      snackbar(err.data.message || "");
    },
  });

  const addUserMutation = useMutation({
    mutationFn: (data) => {
      const basicDetails = getValues();

      return addUser(
        generateFormDataPayload(
          {
            ...data,
            ...basicDetails,
            countryCode: `+${basicDetails.countryCode}`,
            dob: moment(dob).format("YYYY-MM-DD"),
            deviceToken: "dummy",
            deviceType: "web",
          },
          "profile"
        )
      );
    },
    onSuccess: (data) => {
      const decryptResp = decryptData(data);
      localStorage.setItem("hollypills_token", decryptResp?.data?.token);
      setUser(decryptResp?.data?.user);
      snackbar(decryptResp?.message || "", "info");
      navigate("/");
    },
    onError: (err) => {
      const decryptErr = decryptData(err.data);
      snackbar(decryptErr?.message || "");
    },
  });

  return (
    <>
      <Helmet defer={false}>
        <title>Create Account | Join Holypills for Better Health</title>
        <meta
          name="description"
          content="Sign up with Holypills to track your health, connect with doctors, and get expert guidance for women’s wellness and care."
        />
        <meta property="og:image" content="/src/assets/images/ogImage.jpg" />
        <meta name="twitter:image" content="/src/assets/images/ogImage.jpg" />
        <link rel="canonical" href="https://holypills.in/signup" />

        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content="Create Account | Join Holypills for Better Health"
        />
        <meta
          property="og:description"
          content="Sign up with Holypills to track your health, connect with doctors, and get expert guidance for women’s wellness and care."
        />

        <meta property="og:url" content="https://holypills.in/signup" />
        <meta property="og:site_name" content="Holypills"></meta>

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Create Account | Join Holypills for Better Health"
        />
        <meta
          name="twitter:description"
          content="Sign up with Holypills to track your health, connect with doctors, and get expert guidance for women’s wellness and care."
        />

        <meta name="twitter:url" content="https://holypills.in/signup"></meta>
      </Helmet>
      <div className="wrapper_create_profile">
        <div className="container">
          <div className="logo_profile">
            <Link to={"/"}>
              <img src={logodarkimg} alt="logo" />
            </Link>
          </div>

          {step === 1 ? (
            <form
              className="wrap_profile"
              onSubmit={handleSubmit(() => setStep(2))}
            >
              <h2>Create a Profile</h2>
              <div className="wrap_img">
                <figure>
                  <img src={profileImageUrl} alt="image" />
                  <input
                    type="file"
                    id="file"
                    onChange={(e) => setValue("profile", e.target.files[0])}
                  />
                  <label htmlFor="file">
                    <img src={editicon} alt="icon" />
                  </label>
                </figure>
                {errors.profile?.message && (
                  <span className="error">{errors.profile?.message}</span>
                )}
              </div>
              <div className="form_field_wrap">
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Name
                  </label>
                  <input
                    className="input form-control"
                    type="text"
                    placeholder="Enter your Name"
                    {...register("name")}
                  />
                  {errors.name?.message && (
                    <span className="error">{errors.name?.message}</span>
                  )}
                  <img src={profileicon} alt="icon" />
                </div>
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Email
                  </label>
                  <input
                    className="input form-control"
                    type="text"
                    placeholder="Enter your Email"
                    {...register("email")}
                  />
                  {errors.email?.message && (
                    <span className="error">{errors.email?.message}</span>
                  )}
                  <img src={messageicon} alt="icon" />
                </div>
              </div>
              <div className="form_field_wrap">
                <div className="form_field">
                  <label className="label" htmlFor="">
                    Mobile Number
                  </label>
                  <PhoneInput
                    placeholder="Enter Number"
                    country={"in"}
                    disabled={isMobileVerified}
                    value={`${countryCode}${mobile}`}
                    onChange={(fullNumber, dialCodeDetail) => {
                      const mobile = fullNumber.replace(
                        `${dialCodeDetail?.dialCode}`,
                        ""
                      );
                      const countryCode = dialCodeDetail?.dialCode;
                      setValue("mobile", mobile);
                      setValue("countryCode", countryCode);
                    }}
                  />
                  <span
                    className={`verify ${isMobileVerified && "verify_sucess"}`}
                    onClick={() => {
                      if (
                        mobile.length > 4 &&
                        !errors.mobile &&
                        !isMobileVerified
                      ) {
                        setModalShow(true);
                        sendOtpMutation.mutate({ mobile });
                      }
                    }}
                  >
                    {isMobileVerified ? "Verified" : "Verify"}
                  </span>
                  {errors.mobile?.message && (
                    <span className="error">{errors.mobile?.message}</span>
                  )}
                </div>
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Password
                  </label>
                  <input
                    className="input form-control"
                    type={changePassword ? "password" : "text"}
                    placeholder="Enter your password..."
                    {...register("password")}
                  />
                  <span
                    onClick={() => {
                      setChangePassword(changeIcon);
                    }}
                  >
                    {changeIcon ? <FaRegEyeSlash /> : <FaRegEye />}
                  </span>
                  {errors.password?.message && (
                    <span className="error">{errors.password?.message}</span>
                  )}
                  <img src={emailicon} alt="icon" />
                </div>
              </div>
              <div className="form_field">
                <Select
                  className="cstm_select"
                  options={countries}
                  onChange={(e) => setValue("country", e.label)}
                />
                {errors.country?.message && (
                  <span className="error">{errors.country?.message}</span>
                )}
              </div>
              <div className="btn_wrap">
                <button className="button login">
                  Save & Next <FaArrowRightLong />
                </button>
              </div>
            </form>
          ) : (
            <form
              className="wrap_profile additional_profile"
              onSubmit={additionalHandleSubmit(addUserMutation.mutate)}
            >
              <h2>Additional Detail</h2>

              <div className="form_field_wrap">
                <div className="form_field icon_set">
                  <label
                    style={{ display: "block" }}
                    className="label"
                    htmlFor=""
                  >
                    Date Of Birth
                  </label>
                  <DatePicker
                    selected={dob}
                    showMonthDropdown
                    showYearDropdown
                    minDate={new Date(2013, 0, 1)}
                    onChange={(date) => {
                      const dobDate = new Date(date).toISOString();
                      additionalSetValue("dob", dobDate);
                    }}
                    className="input form-control w-100"
                    placeholder="Select DOB"
                  />
                  {additionalErrors.dob?.message && (
                    <span className="error">
                      {additionalErrors.dob?.message}
                    </span>
                  )}
                </div>
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Blood Group
                  </label>
                  <Select
                    className="cstm_select"
                    options={bloodGroups}
                    onChange={(e) => additionalSetValue("bloodGroup", e.label)}
                  />
                  {additionalErrors.bloodGroup?.message && (
                    <span className="error">
                      {additionalErrors.bloodGroup?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="form_field_wrap">
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Height (in cm)
                  </label>
                  <input
                    className="input form-control"
                    type="number"
                    placeholder="Enter your Height"
                    {...additionalRegister("height")}
                  />
                  <span>cm</span>
                  {additionalErrors.height?.message && (
                    <span className="error">
                      {additionalErrors.height?.message}
                    </span>
                  )}
                </div>
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Weight (in kg)
                  </label>
                  <input
                    className="input form-control"
                    type="number"
                    placeholder="Enter your Weight"
                    {...additionalRegister("weight")}
                  />
                  <span>kg</span>
                  {additionalErrors.weight?.message && (
                    <span className="error">
                      {additionalErrors.weight?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="form_field_wrap">
                <div className="form_field">
                  <label className="label" htmlFor="">
                    Emergency Mobile Number
                  </label>
                  <PhoneInput
                    placeholder="Enter Number"
                    country={"in"}
                    value={emergencyContact}
                    onChange={(fullNumber, dialCodeDetail) => {
                      const mobile = fullNumber.replace(
                        `${dialCodeDetail?.dialCode}`,
                        ""
                      );
                      const countryCode = dialCodeDetail?.dialCode;
                      additionalSetValue(
                        "emergencyContact",
                        `${countryCode}${mobile}`
                      );
                    }}
                  />
                  {additionalErrors.emergencyContact?.message && (
                    <span className="error">
                      {additionalErrors.emergencyContact?.message}
                    </span>
                  )}
                </div>
                <div className="form_field icon_set">
                  <label className="label" htmlFor="">
                    Address
                  </label>
                  <input
                    className="input form-control"
                    placeholder="Enter your Address"
                    {...additionalRegister("address")}
                  />
                  {additionalErrors.address?.message && (
                    <span className="error">
                      {additionalErrors.address?.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="btn_wrap">
                <ButtonLoader
                  text={
                    <>
                      Save & Next <FaArrowRightLong />
                    </>
                  }
                  className="button login"
                  isLoading={addUserMutation.isPending}
                />
              </div>
            </form>
          )}
        </div>
        <Modal
          className="cstm_modal"
          show={modalShow}
          size="md"
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title id="contained-modal-title-vcenter">
              Enter the OTP code <br /> sent to you
              <span onClick={() => setModalShow(false)}>
                <RxCross2 />
              </span>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              Code sent to <Link to={""}>{`${countryCode} ${mobile}`}</Link>{" "}
              unless you already have an account
            </p>
            <div className="otp_form" action="">
              <div className="form_field">
                <OTPInput
                  value={otp}
                  onChange={setOtp}
                  numInputs={6}
                  // renderSeparator={<span>-</span>}
                  renderInput={(props) => <input {...props} />}
                />
              </div>
              {seconds < 1 ? (
                <ButtonLoader
                  text={"Resend OTP"}
                  className="login"
                  isLoading={sendOtpMutation.isPending}
                  onClick={() => sendOtpMutation.mutate({ mobile })}
                />
              ) : (
                <p>
                  Resend OTP in <b>00:{seconds}</b> Seconds
                </p>
              )}
              <div className="btn_wrap_login">
                <ButtonLoader
                  text={
                    <>
                      Verify <FaArrowRightLong />
                    </>
                  }
                  className="login"
                  isLoading={verifyOtpMutation.isPending}
                  onClick={() => verifyOtpMutation.mutate({ mobile, otp })}
                />
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default CreateProfile;
